import * as React from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useRetracked } from 'js/lib/retracked';

import { Link } from '@coursera/cds-core';

import { useEnterpriseProperties } from 'bundles/account-profile/common/eventing/useEnterpriseProperties';
import Notification from 'bundles/notification-center/components/Notification';
import type {
  DegreeApplicationDeadlineNotification as DegreeApplicationDeadlineNotificationType,
  Notification as NotificationType,
} from 'bundles/notification-center/types';

import _t from 'i18n!nls/notification-center';

type Props = {
  notification: DegreeApplicationDeadlineNotificationType;
  rank?: number;
  onClick: (notification: NotificationType) => void;
};

const DegreeApplicationDeadlineNotification = ({ notification, rank, onClick }: Props) => {
  const trackComponent = useRetracked();
  const enterpriseProperties = useEnterpriseProperties();

  const { degreeName: programName, degreeSlug, universityName: partnerName } = notification.data;
  const slug = degreeSlug;
  const degreeUrl = `/degrees/${slug}`;
  const lengthOfTime =
    // eslint-disable-next-line no-restricted-syntax
    notification.messageType === 'DegreeDeadlineOneWeekAheadMessage' ? _t('one week') : _t('30 days');

  const degreeLink = (
    <Link href={degreeUrl} variant="quiet">
      {_t('#{partnerName} #{programName}', { partnerName, programName })}
    </Link>
  );

  useEffect(() => {
    if (!notification.isRead) {
      trackComponent({
        action: 'view',
        trackingData: {
          degreeSlug: slug,
          notificationType: notification.messageType,
          isRead: false,
        },
        trackingName: 'degreeApplicationDeadline',
      });
    }
    // only run once on render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Notification
      href={degreeUrl}
      notification={notification}
      rank={rank}
      trackingData={{
        // tracking to Eventing v3
        product: {
          id: notification.data.degreeId,
          name: programName,
          slug,
          type: 'degree' as const,
        },
        enterprise: {
          organizationSlug: enterpriseProperties?.organizationSlug,
          programSlug: enterpriseProperties?.programSlug,
        },
      }}
      onClick={(clickedNotification) => {
        onClick(clickedNotification);
        trackComponent({
          action: 'click',
          trackingData: {
            degreeSlug: slug,
            notificationType: clickedNotification.messageType,
          },
          trackingName: 'degreeApplicationDeadline',
        });
      }}
    >
      <FormattedMessage
        message={_t(
          'The application deadline for the {partnerNameDegreeName} is only {lengthOfTime} away! Don’t delay. Start your application today.'
        )}
        partnerNameDegreeName={degreeLink}
        lengthOfTime={lengthOfTime}
      />
    </Notification>
  );
};

export default DegreeApplicationDeadlineNotification;
